import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDownRight, MessageSquareText, Download } from "lucide-react";
import { useA11y } from "@/context/A11yContext";
import { scrollToId } from "@/lib/scroll";

const ease = [0.22, 1, 0.36, 1];
const LINES = [{ text: "Practical SEND" }, { text: "leadership," }, { text: "made simple.", accent: true }];

const MaskedLine = ({ line, i }) => (
  <span className="block overflow-hidden pb-[0.1em]">
    <motion.span className={`block ${line.accent ? "italic text-brand" : ""}`}
      initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.15, ease, delay: 0.2 + i * 0.13 }}>
      {line.text}
    </motion.span>
  </span>
);

const Badge = () => (
  <div className="absolute -right-3 -top-3 z-10 h-28 w-28 sm:-right-6 sm:h-32 sm:w-32" aria-hidden="true">
    <div className="absolute inset-0 rounded-full bg-sun" />
    <svg viewBox="0 0 100 100" className="spin-slow absolute inset-0 h-full w-full text-sun-fg">
      <defs><path id="circ" d="M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0" /></defs>
      <text fontSize="9.6" fontWeight="700" letterSpacing="2.2" fill="currentColor">
        <textPath href="#circ">FREE · NO SIGN-UP · PRINT OR PDF · </textPath>
      </text>
    </svg>
    <span className="absolute inset-0 flex items-center justify-center font-display text-3xl italic text-sun-fg">01</span>
  </div>
);

const RagKey = () => (
  <div style={{ transform: "translateZ(70px)" }}
    className="absolute -bottom-6 left-3 z-10 w-[230px] rounded-2xl border-2 border-line bg-surface p-4 shadow-[0_30px_60px_-30px_rgba(0,0,0,.45)] sm:-left-10">
    <p className="eyebrow mb-3">RAG self-review</p>
    <ul className="space-y-2 text-sm">
      <li className="flex items-center gap-2"><span className="dot dot-green" />Working well</li>
      <li className="flex items-center gap-2"><span className="dot dot-amber" />Review needed</li>
      <li className="flex items-center gap-2"><span className="dot dot-red" />Priority action</li>
    </ul>
  </div>
);

const HeroVisual = () => {
  const { settings } = useA11y();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-9%", "9%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 16 });
  const onMove = (e) => {
    if (settings.reduceMotion) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className="relative mx-auto w-full max-w-[480px] [perspective:1200px]" data-testid="hero-visual">
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.3, ease, delay: 0.35 }} className="relative">
        <div className="relative aspect-[4/5] overflow-hidden rounded-b-[2.5rem] rounded-t-[999px] bg-brand">
          <motion.img src="/img/hero.jpg" alt="A teacher sitting with a pupil and supporting their learning at a classroom table"
            style={{ y: settings.reduceMotion ? 0 : imgY, scale: 1.2 }} className="h-full w-full object-cover" />
          <div className="hero-spot absolute inset-0" aria-hidden="true" />
        </div>
        <Badge />
        <RagKey />
      </motion.div>
    </div>
  );
};

const STATS = [
  { k: "2", v: "free toolkits ready now" },
  { k: "4", v: "toolkits for schools" },
  { k: "0", v: "sign-ups or logins" },
];

export const Hero = () => (
  <section id="top" aria-labelledby="hero-title" className="relative overflow-hidden pb-24 pt-[150px] lg:pb-32 lg:pt-[178px]">
    <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-5 sm:px-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:px-10">
      <div>
        <motion.p className="eyebrow mb-7 flex items-center gap-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          <span className="h-px w-10 bg-ink" aria-hidden="true" /> Free SENDCO Resource Pack 2026 · No sign-up needed
        </motion.p>
        <h1 id="hero-title" className="font-display text-[2.85rem] font-medium leading-[0.98] tracking-[-0.035em] sm:text-6xl lg:text-[5.4rem]">
          {LINES.map((l, i) => <MaskedLine key={l.text} line={l} i={i} />)}
        </h1>
        <motion.p className="mt-8 max-w-xl text-base leading-relaxed text-ink-soft sm:text-lg"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.7 }}>
          Practical tools, audits and templates for SENDCOs, inclusion leads, headteachers, school leaders and governors.
          Open a section, fill it in on screen, then print it or download it as a PDF.
        </motion.p>
        <motion.div className="mt-10 flex flex-wrap gap-3" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.85 }}>
          <Link to="/workbook" className="btn btn-brand text-base" data-testid="hero-download-workbook">
            <Download size={18} aria-hidden /> Download Workbook
          </Link>
          <a href="#toolkit-one" onClick={(e) => { e.preventDefault(); scrollToId("toolkit-one"); }} className="btn btn-ghost text-base" data-testid="hero-open-toolkit">
            Open the full pack, free <ArrowDownRight size={18} aria-hidden />
          </a>
          <Link to="/feedback" className="btn btn-ghost text-base" data-testid="hero-feedback">
            <MessageSquareText size={18} aria-hidden /> Give feedback
          </Link>
        </motion.div>
        <dl className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-line pt-6">
          {STATS.map((s) => (
            <div key={s.v}>
              <dt className="sr-only">{s.v}</dt>
              <dd className="font-display text-4xl font-medium">{s.k}</dd>
              <dd className="mt-1 text-sm text-ink-soft">{s.v}</dd>
            </div>
          ))}
        </dl>
      </div>
      <HeroVisual />
    </div>
  </section>
);
